/**
 * useCreditBalance — S26 self-readable evaluation balance hook
 *
 * useMyChannelCreditBalance(slug) fetches the current authenticated user's
 * own evaluation credit balance for the specified channel.
 *
 * Degradation contract:
 *   - Returns undefined data when the endpoint is unavailable (ledger flag off,
 *     404, network error).  Callers must handle undefined without throwing.
 *   - Never blocks submit or UI flows when data is loading or undefined.
 */

import { useQuery } from '@tanstack/react-query';
import { getMemberEvaluationBalance } from '@/services/channelApi';
import type { MemberEvaluationBalance } from '@/services/channelApi';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Fetch the current user's own evaluation credit balance for a channel.
 *
 * @param slug - channel slug to query (may be null/undefined to disable)
 * @returns React Query result; data is MemberEvaluationBalance or undefined on error/loading.
 *
 * membership_status from the response is the canonical approved-membership signal
 * for S28 (resolves Open Q4 — no separate membership query needed).
 */
export function useMyChannelCreditBalance(slug: string | null | undefined) {
  const { user } = useAuth();
  const userId = user?.id;

  return useQuery<MemberEvaluationBalance>({
    queryKey: ['channel-credit-balance', slug, userId],
    queryFn: () => getMemberEvaluationBalance(slug!, userId!),
    enabled: !!slug && !!userId,
    // Stale time: 60 s — balance changes only on eval submit or coach allocation,
    // neither of which happens at high frequency from this surface.
    staleTime: 60_000,
    // Do not retry on error: a 404 (flag off) or 403 (wrong user) should degrade
    // gracefully rather than retrying and blocking the UI.
    retry: false,
  });
}
